import { css, DefaultTheme, FlattenInterpolation, ThemeProps } from 'styled-components';

export type FAQStyleProps = {
    $open: boolean;
    $accent: string;
    $index: number;
};

type StyleProps = FAQStyleProps & ThemeProps<DefaultTheme>;
type Rule = FlattenInterpolation<StyleProps>;

export type FAQIndicator =
    | 'plus'
    | 'chevron'
    | 'caret'
    | 'arrow'
    | 'bracket'
    | 'slash'
    | 'square'
    | 'none';

export type FAQMeta = 'label' | 'index' | 'status';

export type FAQVariant = {
    id: number;
    label: string;
    indicator: FAQIndicator;
    indicatorSide?: 'left' | 'right';
    numbered?: boolean;
    meta?: FAQMeta;
    section?: Rule;
    item?: Rule;
    row?: Rule;
    question?: Rule;
    answer?: Rule;
    collapse?: Rule;
    indicatorStyle?: Rule;
    number?: Rule;
    metaStyle?: Rule;
};

const hairline = css<StyleProps>`
    border-bottom: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
    &:last-child {
        border-bottom: none;
    }
`;

/* Matches SectionHeading's marker column: 0.8em swatch + 0.25em margin + 0.2em column gap. */
const SECTION_GUTTER = '1.25rem';

export const FAQ_VARIANTS: FAQVariant[] = [
    {
        id: 1,
        label: '01 — hairline rows',
        indicator: 'plus',
        item: css<StyleProps>`
            ${hairline}
            padding: 1.4em 0;
            transition: padding-left 180ms ease;
            &:hover {
                padding-left: 0.4em;
            }
        `,
        question: css<StyleProps>`
            color: ${({ $open, $accent, theme }) => ($open ? $accent : theme.colour.OFF_WHITE)};
            transition: color 180ms ease;
        `,
        indicatorStyle: css<StyleProps>`
            color: ${({ $open, $accent, theme }) => ($open ? $accent : theme.colour.LIGHT_GREY)};
        `,
    },
    {
        id: 2,
        label: '02 — bordered panel',
        indicator: 'chevron',
        section: css<StyleProps>`
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            overflow: hidden;
            background: ${({ theme }) => theme.colour.LIGHT_GREY};
            gap: 1px;
        `,
        item: css<StyleProps>`
            background: ${({ theme }) => theme.colour.DARK_GREY};
            padding: 1.5em 1.6em;
        `,
        indicatorStyle: css<StyleProps>`
            color: ${({ $accent }) => $accent};
        `,
    },
    {
        id: 3,
        label: '03 — terminal log',
        indicator: 'none',
        section: css<StyleProps>`
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            padding: 1.5em 1.6em;
            gap: 0.9em;
        `,
        row: css<StyleProps>`
            font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
            font-size: 0.95em;
            font-weight: 400;
            gap: 0.6em;
            &::before {
                content: '$';
                color: ${({ $accent }) => $accent};
            }
        `,
        question: css<StyleProps>`
            color: ${({ theme }) => theme.colour.OFF_WHITE};
        `,
        answer: css<StyleProps>`
            font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
            font-size: 0.85em;
            padding-left: 1.4em;
            border-left: 1px solid ${({ $accent }) => $accent};
            margin-left: 0.2em;
        `,
    },
    {
        id: 4,
        label: '04 — numbered index',
        indicator: 'plus',
        numbered: true,
        item: css<StyleProps>`
            ${hairline}
            padding: 1.6em 0;
        `,
        number: css<StyleProps>`
            font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
            font-size: 0.8em;
            color: ${({ $accent }) => $accent};
            width: 3em;
            flex: none;
        `,
        question: css<StyleProps>`
            font-family: ${({ theme }) => theme.font.FONT_SERIF_ROBOTO_SLAB};
            font-weight: 700;
            font-size: 1.15em;
        `,
        answer: css<StyleProps>`
            padding-left: 3em;
        `,
    },
    {
        id: 5,
        label: '05 — accent bar',
        indicator: 'plus',
        section: css<StyleProps>`
            gap: 0.5em;
        `,
        item: css<StyleProps>`
            padding: 1.3em 1.4em;
            border-left: 3px solid
                ${({ $open, $accent, theme }) => ($open ? $accent : theme.colour.LIGHT_GREY)};
            background: ${({ $open }) => ($open ? 'rgba(128, 128, 140, 0.08)' : 'transparent')};
            transition: border-color 200ms ease, background 200ms ease;
            &:hover {
                border-left-color: ${({ $accent }) => $accent};
            }
        `,
    },
    {
        id: 6,
        label: '06 — tile grid',
        indicator: 'plus',
        section: css<StyleProps>`
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1px;
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            overflow: hidden;
            @media (max-width: 50rem) {
                grid-template-columns: 1fr;
            }
        `,
        item: css<StyleProps>`
            background: ${({ theme }) => theme.colour.DARK_GREY};
            box-shadow: 0 0 0 1px ${({ theme }) => theme.colour.LIGHT_GREY};
            padding: 1.5em 1.4em;
        `,
        row: css<StyleProps>`
            align-items: flex-start;
        `,
        question: css<StyleProps>`
            font-size: 1em;
            line-height: 1.35;
        `,
    },
    {
        id: 7,
        label: '07 — editorial serif',
        indicator: 'arrow',
        item: css<StyleProps>`
            ${hairline}
            padding: 2.2em 0;
        `,
        question: css<StyleProps>`
            font-family: ${({ theme }) => theme.font.FONT_SERIF_ROBOTO_SLAB};
            font-weight: 700;
            font-size: 1.6em;
            line-height: 1.25;
        `,
        answer: css<StyleProps>`
            max-width: 46em;
            font-size: 1em;
        `,
        indicatorStyle: css<StyleProps>`
            color: ${({ $accent }) => $accent};
            font-size: 1.4em;
        `,
    },
    {
        id: 8,
        label: '08 — split column',
        indicator: 'none',
        numbered: true,
        item: css<StyleProps>`
            ${hairline}
            padding: 1.8em 0;
        `,
        row: css<StyleProps>`
            display: grid;
            grid-template-columns: 10rem 1fr;
            align-items: start;
            @media (max-width: 50rem) {
                grid-template-columns: 1fr;
                gap: 0.4em;
            }
        `,
        number: css<StyleProps>`
            font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
            font-size: 0.75em;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: ${({ $accent }) => $accent};
            &::before {
                content: 'Q';
            }
        `,
        answer: css<StyleProps>`
            padding-left: 10rem;
            @media (max-width: 50rem) {
                padding-left: 0;
            }
        `,
    },
    {
        id: 9,
        label: '09 — pill rows',
        indicator: 'chevron',
        section: css<StyleProps>`
            gap: 0.6em;
        `,
        item: css<StyleProps>`
            border: 1px solid
                ${({ $open, $accent, theme }) => ($open ? $accent : theme.colour.LIGHT_GREY)};
            border-radius: 999px;
            padding: 1.1em 1.8em;
            transition: border-color 180ms ease, background 180ms ease;
            &:hover {
                border-color: ${({ $accent }) => $accent};
            }
            ${({ $open }) => $open && 'border-radius: 1.6em;'}
        `,
        answer: css<StyleProps>`
            padding-right: 2em;
        `,
    },
    {
        id: 10,
        label: '10 — underline draw',
        indicator: 'caret',
        section: css<StyleProps>`
            gap: 0.2em;
        `,
        item: css<StyleProps>`
            padding: 1.4em 0 1.5em 0;
            position: relative;
            &::after {
                content: '';
                position: absolute;
                left: 0;
                bottom: 0;
                height: 2px;
                width: ${({ $open }) => ($open ? '100%' : '0%')};
                background: ${({ $accent }) => $accent};
                transition: width 320ms cubic-bezier(0.2, 0, 0, 1);
            }
            &::before {
                content: '';
                position: absolute;
                left: 0;
                bottom: 0;
                height: 1px;
                width: 100%;
                background: ${({ theme }) => theme.colour.LIGHT_GREY};
            }
        `,
    },
    {
        id: 11,
        label: '11 — mono brackets',
        indicator: 'bracket',
        section: css<StyleProps>`
            border-top: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
        `,
        item: css<StyleProps>`
            ${hairline}
            padding: 1.4em 0;
        `,
        row: css<StyleProps>`
            text-transform: uppercase;
            letter-spacing: 0.06em;
            font-size: 0.95em;
        `,
        indicatorStyle: css<StyleProps>`
            color: ${({ $accent }) => $accent};
        `,
        answer: css<StyleProps>`
            text-transform: none;
            letter-spacing: 0;
        `,
    },
    {
        id: 12,
        label: '12 — filled panel',
        indicator: 'plus',
        section: css<StyleProps>`
            gap: 0.5em;
        `,
        item: css<StyleProps>`
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            padding: 1.4em 1.5em;
            background: ${({ $open }) =>
                $open ? 'rgba(128, 128, 140, 0.14)' : 'rgba(128, 128, 140, 0.06)'};
            transition: background 200ms ease;
            &:hover {
                background: rgba(128, 128, 140, 0.14);
            }
        `,
        indicatorStyle: css<StyleProps>`
            color: ${({ $accent }) => $accent};
        `,
    },
    {
        id: 13,
        label: '13 — section swatch',
        indicator: 'none',
        item: css<StyleProps>`
            ${hairline}
            padding: 1.5em 0;
        `,
        row: css<StyleProps>`
            gap: 0.8em;
            text-transform: uppercase;
            font-size: 0.95em;
            letter-spacing: 0.02em;
            &::before {
                content: '';
                width: 0.8em;
                height: 0.8em;
                flex: none;
                background: ${({ $accent }) => $accent};
                transform: ${({ $open }) => ($open ? 'rotate(45deg)' : 'rotate(0deg)')};
                transition: transform 240ms cubic-bezier(0.2, 0, 0, 1);
            }
        `,
        answer: css<StyleProps>`
            padding-left: 1.6em;
            text-transform: none;
        `,
    },
    {
        id: 14,
        label: '14 — dashed ghost',
        indicator: 'plus',
        section: css<StyleProps>`
            gap: 0.4em;
        `,
        item: css<StyleProps>`
            border: 1px dashed
                ${({ $open, $accent, theme }) => ($open ? $accent : theme.colour.LIGHT_GREY)};
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            padding: 1.3em 1.4em;
            transition: border-color 180ms ease;
            &:hover {
                border-color: ${({ $accent }) => $accent};
            }
        `,
        answer: css<StyleProps>`
            border-top: 1px dashed ${({ theme }) => theme.colour.LIGHT_GREY};
            margin-top: 0.4em;
            padding-top: 0.9em;
        `,
    },
    {
        id: 15,
        label: '15 — slide on hover',
        indicator: 'arrow',
        item: css<StyleProps>`
            ${hairline}
            padding: 1.5em 0;
            position: relative;
            transform: translateX(${({ $open }) => ($open ? '0.8em' : '0')});
            transition: transform 220ms cubic-bezier(0.2, 0, 0, 1);
            &::before {
                content: '';
                position: absolute;
                left: -0.8em;
                top: 1.5em;
                width: 2px;
                height: 1.2em;
                background: ${({ $accent }) => $accent};
                opacity: ${({ $open }) => ($open ? 1 : 0)};
                transition: opacity 220ms ease;
            }
            &:hover {
                transform: translateX(0.8em);
            }
        `,
    },
    {
        id: 16,
        label: '16 — ghost numerals',
        indicator: 'plus',
        numbered: true,
        item: css<StyleProps>`
            ${hairline}
            padding: 1.8em 0;
            position: relative;
            overflow: hidden;
        `,
        number: css<StyleProps>`
            position: absolute;
            right: 2.5em;
            top: 0.1em;
            font-family: ${({ theme }) => theme.font.FONT_SERIF_ROBOTO_SLAB};
            font-size: 3.4em;
            font-weight: 700;
            line-height: 1;
            color: ${({ $accent }) => $accent};
            opacity: ${({ $open }) => ($open ? 0.35 : 0.12)};
            transition: opacity 240ms ease;
            pointer-events: none;
        `,
        question: css<StyleProps>`
            position: relative;
            z-index: 1;
            max-width: 70%;
        `,
    },
    {
        id: 17,
        label: '17 — code block answer',
        indicator: 'chevron',
        item: css<StyleProps>`
            ${hairline}
            padding: 1.4em 0;
        `,
        answer: css<StyleProps>`
            font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
            font-size: 0.82em;
            line-height: 1.7;
            background: rgba(128, 128, 140, 0.1);
            border-left: 3px solid ${({ $accent }) => $accent};
            border-radius: 0 ${({ theme }) => theme.BORDER_RADIUS}
                ${({ theme }) => theme.BORDER_RADIUS} 0;
            padding: 1em 1.2em;
            margin-top: 0.9em;
        `,
    },
    {
        id: 18,
        label: '18 — comment slash',
        indicator: 'slash',
        indicatorSide: 'left',
        section: css<StyleProps>`
            gap: 0.9em;
        `,
        item: css<StyleProps>`
            padding: 0.6em 0;
        `,
        row: css<StyleProps>`
            font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
            font-weight: 400;
            font-size: 0.95em;
            gap: 0;
        `,
        indicatorStyle: css<StyleProps>`
            color: ${({ $accent }) => $accent};
            width: ${SECTION_GUTTER};
            font-size: 0.85em;
            justify-content: flex-start;
        `,
        answer: css<StyleProps>`
            margin-left: ${SECTION_GUTTER};
            padding-left: 1.2em;
            border-left: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
        `,
    },
    {
        id: 19,
        label: '19 — two-tone rows',
        indicator: 'caret',
        indicatorSide: 'left',
        section: css<StyleProps>`
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            overflow: hidden;
        `,
        item: css<StyleProps>`
            padding: 1.3em 1.6em;
            background: ${({ $index }) =>
                $index % 2 === 0 ? 'transparent' : 'rgba(128, 128, 140, 0.08)'};
        `,
        indicatorStyle: css<StyleProps>`
            color: ${({ $accent }) => $accent};
        `,
        answer: css<StyleProps>`
            padding-left: 1.9em;
        `,
    },
    {
        id: 20,
        label: '20 — corner frame',
        indicator: 'square',
        section: css<StyleProps>`
            gap: 0.9em;
        `,
        item: css<StyleProps>`
            position: relative;
            padding: 1.5em 1.6em;
            border: 1px solid rgba(128, 128, 140, 0.35);
            &::before,
            &::after {
                content: '';
                position: absolute;
                width: 0.7em;
                height: 0.7em;
                border: 1px solid ${({ $accent }) => $accent};
                opacity: ${({ $open }) => ($open ? 1 : 0.5)};
                transition: opacity 200ms ease;
            }
            &::before {
                top: -1px;
                left: -1px;
                border-right: none;
                border-bottom: none;
            }
            &::after {
                bottom: -1px;
                right: -1px;
                border-left: none;
                border-top: none;
            }
        `,
        row: css<StyleProps>`
            text-transform: uppercase;
            font-size: 0.95em;
            letter-spacing: 0.04em;
        `,
        answer: css<StyleProps>`
            text-transform: none;
            letter-spacing: 0;
        `,
    },
];

/* ---- 21-30: comment-slash (18) with treatments for the empty right-hand side ---- */

const SLASH_SECTION = css<StyleProps>`
    gap: 0.9em;
`;

const SLASH_ITEM = css<StyleProps>`
    padding: 0.6em 0;
`;

const SLASH_ROW = css<StyleProps>`
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-weight: 400;
    font-size: 0.95em;
    gap: 0;
    padding-left: ${SECTION_GUTTER};
`;

const SLASH_INDICATOR = css<StyleProps>`
    color: ${({ $accent }) => $accent};
    width: ${SECTION_GUTTER};
    margin-left: -${SECTION_GUTTER};
    font-size: 0.85em;
    justify-content: flex-start;
`;

const SLASH_ANSWER = css<StyleProps>`
    margin-left: ${SECTION_GUTTER};
    padding-left: 1.2em;
    border-left: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
`;

const META_TEXT = css<StyleProps>`
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 0.78em;
    text-transform: uppercase;
    letter-spacing: 0.12em;
`;

const SLASH_VARIANTS: FAQVariant[] = [
    {
        id: 21,
        label: '21 — right meta tag',
        indicator: 'slash',
        indicatorSide: 'left',
        meta: 'label',
        section: SLASH_SECTION,
        item: SLASH_ITEM,
        row: SLASH_ROW,
        indicatorStyle: SLASH_INDICATOR,
        answer: SLASH_ANSWER,
        metaStyle: css<StyleProps>`
            ${META_TEXT}
            margin-left: auto;
            color: ${({ $accent }) => $accent};
            opacity: ${({ $open }) => ($open ? 1 : 0.7)};
            transition: opacity 180ms ease;
            &::before {
                content: '// ';
            }
        `,
    },
    {
        id: 22,
        label: '22 — dotted leader to tag',
        indicator: 'slash',
        indicatorSide: 'left',
        meta: 'label',
        section: SLASH_SECTION,
        item: SLASH_ITEM,
        row: css<StyleProps>`
            ${SLASH_ROW}
            &::after {
                content: '';
                order: 1;
                flex: 1;
                min-width: 2em;
                margin: 0 0.7em;
                border-bottom: 1px dotted ${({ theme }) => theme.colour.LIGHT_GREY};
                position: relative;
                bottom: 0.35em;
            }
        `,
        question: css<StyleProps>`
            flex: 0 1 auto;
        `,
        indicatorStyle: SLASH_INDICATOR,
        answer: SLASH_ANSWER,
        metaStyle: css<StyleProps>`
            ${META_TEXT}
            order: 2;
            color: ${({ $accent }) => $accent};
        `,
    },
    {
        id: 23,
        label: '23 — hairline leader to index',
        indicator: 'slash',
        indicatorSide: 'left',
        meta: 'index',
        section: SLASH_SECTION,
        item: SLASH_ITEM,
        row: css<StyleProps>`
            ${SLASH_ROW}
            &::after {
                content: '';
                order: 1;
                flex: 1;
                min-width: 2em;
                margin: 0 0.7em;
                height: 1px;
                background: ${({ theme }) => theme.colour.LIGHT_GREY};
                opacity: 0.6;
            }
        `,
        question: css<StyleProps>`
            flex: 0 1 auto;
        `,
        indicatorStyle: SLASH_INDICATOR,
        answer: SLASH_ANSWER,
        metaStyle: css<StyleProps>`
            ${META_TEXT}
            order: 2;
            letter-spacing: 0.06em;
            color: ${({ $open, $accent, theme }) => ($open ? $accent : theme.colour.LIGHT_GREY)};
            transition: color 180ms ease;
            &::before {
                content: '[';
            }
            &::after {
                content: ']';
            }
        `,
    },
    {
        id: 24,
        label: '24 — right status word',
        indicator: 'slash',
        indicatorSide: 'left',
        meta: 'status',
        section: SLASH_SECTION,
        item: SLASH_ITEM,
        row: SLASH_ROW,
        indicatorStyle: SLASH_INDICATOR,
        answer: SLASH_ANSWER,
        metaStyle: css<StyleProps>`
            ${META_TEXT}
            margin-left: auto;
            color: ${({ $open, $accent, theme }) => ($open ? $accent : theme.colour.LIGHT_GREY)};
            transition: color 180ms ease;
            &::before {
                content: '[ ';
            }
            &::after {
                content: ' ]';
            }
        `,
    },
    {
        id: 25,
        label: '25 — two-column rows',
        indicator: 'slash',
        indicatorSide: 'left',
        section: css<StyleProps>`
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            column-gap: 3.5em;
            row-gap: 0.9em;
            align-items: start;
            @media (max-width: 50rem) {
                grid-template-columns: 1fr;
            }
        `,
        item: SLASH_ITEM,
        row: css<StyleProps>`
            ${SLASH_ROW}
            align-items: flex-start;
        `,
        indicatorStyle: css<StyleProps>`
            ${SLASH_INDICATOR}
            padding-top: 0.15em;
        `,
        answer: SLASH_ANSWER,
    },
    {
        id: 26,
        label: '26 — answer in right column',
        indicator: 'slash',
        indicatorSide: 'left',
        section: SLASH_SECTION,
        item: css<StyleProps>`
            display: grid;
            grid-template-columns: 1fr 1fr;
            column-gap: 3em;
            align-items: start;
            padding: 0.7em 0;
            @media (max-width: 50rem) {
                grid-template-columns: 1fr;
            }
        `,
        row: css<StyleProps>`
            ${SLASH_ROW}
            align-items: flex-start;
        `,        collapse: css<StyleProps>`
            @media (min-width: 50.0625rem) {
                grid-column: 2;
                grid-row: 1;
            }
        `,
        indicatorStyle: css<StyleProps>`
            ${SLASH_INDICATOR}
            padding-top: 0.15em;
        `,
        answer: css<StyleProps>`
            margin-left: 0;
            padding-top: 0;
            padding-left: 1.2em;
            border-left: 1px solid ${({ $accent }) => $accent};
        `,
    },
    {
        id: 27,
        label: '27 — right chip',
        indicator: 'slash',
        indicatorSide: 'left',
        meta: 'label',
        section: SLASH_SECTION,
        item: SLASH_ITEM,
        row: SLASH_ROW,
        indicatorStyle: SLASH_INDICATOR,
        answer: SLASH_ANSWER,
        metaStyle: css<StyleProps>`
            ${META_TEXT}
            margin-left: auto;
            font-size: 0.7em;
            color: ${({ $accent }) => $accent};
            border: 1px solid currentColor;
            border-radius: 2px;
            padding: 0.3em 0.7em;
            opacity: ${({ $open }) => ($open ? 1 : 0.75)};
            transition: opacity 180ms ease;
        `,
    },
    {
        id: 28,
        label: '28 — right rail',
        indicator: 'slash',
        indicatorSide: 'left',
        meta: 'index',
        section: css<StyleProps>`
            ${SLASH_SECTION}
            border-right: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            padding-right: 1.4em;
        `,
        item: SLASH_ITEM,
        row: SLASH_ROW,
        indicatorStyle: SLASH_INDICATOR,
        answer: SLASH_ANSWER,
        metaStyle: css<StyleProps>`
            ${META_TEXT}
            margin-left: auto;
            letter-spacing: 0.06em;
            color: ${({ $open, $accent, theme }) => ($open ? $accent : theme.colour.LIGHT_GREY)};
            transition: color 180ms ease;
            &::before {
                content: '#';
                opacity: 0.6;
            }
        `,
    },
    {
        id: 29,
        label: '29 — right accent block',
        indicator: 'slash',
        indicatorSide: 'left',
        section: SLASH_SECTION,
        item: css<StyleProps>`
            ${SLASH_ITEM}
            position: relative;
            padding-right: 2.4em;
            &::after {
                content: '';
                position: absolute;
                right: 0;
                top: 0.85em;
                width: 0.55em;
                height: ${({ $open }) => ($open ? '2.2em' : '0.55em')};
                background: ${({ $accent }) => $accent};
                opacity: ${({ $open }) => ($open ? 1 : 0.55)};
                transition: height 260ms cubic-bezier(0.2, 0, 0, 1), opacity 200ms ease;
            }
        `,
        row: SLASH_ROW,
        indicatorStyle: SLASH_INDICATOR,
        answer: SLASH_ANSWER,
    },
    {
        id: 30,
        label: '30 — ellipsis and close marker',
        indicator: 'slash',
        indicatorSide: 'left',
        section: SLASH_SECTION,
        item: SLASH_ITEM,
        row: css<StyleProps>`
            ${SLASH_ROW}
            &::after {
                content: ${({ $open }) => ($open ? "'{'" : "'...'")};
                margin-left: auto;
                color: ${({ theme }) => theme.colour.LIGHT_GREY};
                letter-spacing: 0.2em;
            }
        `,
        indicatorStyle: SLASH_INDICATOR,
        answer: css<StyleProps>`
            ${SLASH_ANSWER}
            &::after {
                content: ' */';
                color: ${({ $accent }) => $accent};
            }
        `,
    },
];

FAQ_VARIANTS.push(...SLASH_VARIANTS);

export const DEFAULT_FAQ_VARIANT = 11;

export const getFAQVariant = (id?: number): FAQVariant =>
    FAQ_VARIANTS.find((variant) => variant.id === id) ??
    FAQ_VARIANTS.find((variant) => variant.id === DEFAULT_FAQ_VARIANT) ??
    FAQ_VARIANTS[0];
